import { Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
] as const;

type LanguageCode = typeof languages[number]['code'];

export const LanguageSelector = () => {
  const { language, setLanguage, switchLanguagePath } = useLanguage();
  const navigate = useNavigate();
  const currentLang = languages.find(lang => lang.code === language);

  const handleLanguageChange = (langCode: LanguageCode) => {
    if (langCode === language) return;
    const newPath = switchLanguagePath(langCode);
    // Preserve query string and hash across the language switch.
    const { search, hash } = window.location;
    setLanguage(langCode);
    navigate(`${newPath}${search}${hash}`, { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="gap-2 text-foreground hover:text-primary transition-colors"
        >
          <Globe className="h-4 w-4" />
          <span className="hidden md:inline">{currentLang?.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-background border-border z-50">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`gap-3 cursor-pointer ${
              language === lang.code ? 'bg-accent' : ''
            }`}
          >
            <span className="text-lg">{lang.flag}</span>
            <span>{lang.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
